'use client'

import { handleError } from 'api/error'
import useSetSearchParams from 'app/hooks/useSetSearchParams'
import { IconLoader } from 'components/icons'
import LoadingAnimation from 'components/layout/LoadingAnimation'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from 'components/ui/alert-dialog'
import { Button } from 'components/ui/button'
import { FC, useEffect, useRef, useState } from 'react'
import { useFormStatus } from 'react-dom'
import { AnySchema, ValidationError } from 'yup'
import {
    FIELD_COMPONENTS,
    getExtraPropsForField,
    getFieldValidationErrors,
    getInitialValue,
    getNewArrayItem,
    cloneArrayItemForDuplicate
} from './FieldTypes'
import type { FieldRendererProps } from './FieldTypes'

export interface IFormConfig extends IBaseFormConfig {
    validationSchema?: AnySchema
    onFieldChange?: (fieldName: string, value: any, allFormState: { [key: string]: any }) => void
    onValueChange?: (changedValues: { [key: string]: any }, allFormState: { [key: string]: any }) => void
    keepLoadingOnSuccess?: boolean
    /** Return a translated or custom message for API errors. Used when showing toast. */
    getErrorDisplayMessage?: (result: ApiResponse) => string | null
}

const GenericForm: FC<IFormConfig> = ({
    fields,
    onSubmit,
    buttonTexts,
    id,
    parentId,
    urlParam,
    validationSchema,
    keepLoadingOnSuccess,
    onFieldChange,
    onValueChange,
    columns = 1,
    getErrorDisplayMessage
}) => {
    const turnstileRef = useRef<any>(null)
    const { setParam, addParams, removeSpecificParam } = useSetSearchParams()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [updatingCheckboxes, setUpdatingCheckboxes] = useState<{ [key: string]: boolean }>({})
    const [confirmationDialog, setConfirmationDialog] = useState<{
        open: boolean
        field: TFormField | null
        value: boolean
        title: string
        message: string
    }>({
        open: false,
        field: null,
        value: false,
        title: '',
        message: ''
    })

    const [formState, setFormState] = useState<{ [key: string]: any }>({})
    const [changedValues, setChangedValues] = useState<{ [key: string]: any }>({})
    const [errors, setErrors] = useState<{ [key: string]: string }>({})
    const initializedFieldsRef = useRef<Set<string>>(new Set())
    const { pending } = useFormStatus()

    useEffect(() => {
        setFormState(prevState => {
            const newState: { [key: string]: any } = { ...prevState }
            let hasChanges = false

            fields.forEach(field => {
                // Only initialize fields that haven't been initialized yet
                if (!initializedFieldsRef.current.has(field.name)) {
                    hasChanges = true
                    initializedFieldsRef.current.add(field.name)
                    newState[field.name] = getInitialValue(field)
                }
            })

            // Remove fields that no longer exist in the fields array
            const currentFieldNames = new Set(fields.map(f => f.name))
            Object.keys(newState).forEach(key => {
                if (!currentFieldNames.has(key) && initializedFieldsRef.current.has(key)) {
                    delete newState[key]
                    initializedFieldsRef.current.delete(key)
                    hasChanges = true
                }
            })

            return hasChanges ? newState : prevState
        })
    }, [fields])

    const handleChange = (name: string, value: any, field?: TFormField) => {
        const newFormState = {
            ...formState,
            [name]: value
        }

        setFormState(newFormState)

        // Track changed values
        const newChangedValues = {
            ...changedValues,
            [name]: value
        }
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }))
        }

        setChangedValues(newChangedValues)

        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }))
        }

        if (field?.addValueToParams) {
            addParams({ [name]: value.toString() })
        }

        if (onFieldChange) {
            onFieldChange(name, value, newFormState)
        }

        if (onValueChange) {
            onValueChange(newChangedValues, newFormState)
        }
    }

    const handleArrayChange = (name: string, index: number, field: TFormField, value: any) => {
        const updatedArray = [...formState[name]]
        updatedArray[index] = value
        handleChange(name, updatedArray, field)
    }

    const handleAddArrayItem = (name: string, field: TFormField) => {
        const newItem = getNewArrayItem(field)
        const updatedArray = [...(formState[name] as unknown[]), newItem]
        handleChange(name, updatedArray, field)
    }

    const handleRemoveArrayItem = (name: string, index: number, field: TFormField) => {
        const updatedArray = formState[name].filter((_: any, i: number) => i !== index)
        handleChange(name, updatedArray, field)
    }

    const handleDuplicateArrayItem = (name: string, index: number, field: TFormField) => {
        const arr = formState[name] as unknown[]
        const itemToDuplicate = cloneArrayItemForDuplicate(field, arr[index])
        const updatedArray = [...arr]
        updatedArray.splice(index + 1, 0, itemToDuplicate)
        handleChange(name, updatedArray, field)
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setIsSubmitting(true)

        if (validationSchema) {
            try {
                await validationSchema.validate(formState, { abortEarly: false })
                setErrors({})
            } catch (error: any) {
                if (error instanceof ValidationError) {
                    const validationErrors: { [key: string]: string } = {}
                    error.inner.forEach((err: any) => {
                        if (err.path) {
                            validationErrors[err.path] = err.message
                        }
                    })
                    setErrors(validationErrors)
                }
                setIsSubmitting(false)
                return
            }
        }

        const customErrors = getFieldValidationErrors(fields, formState)
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            setIsSubmitting(false)
            return
        }

        const formData = new FormData()

        // Helper function to extract File objects from nested structures
        const extractFiles = (obj: any, prefix: string = ''): { [key: string]: File } => {
            const files: { [key: string]: File } = {}

            if (obj instanceof File) {
                files[prefix] = obj
                return files
            }

            if (Array.isArray(obj)) {
                obj.forEach((item, index) => {
                    const nestedFiles = extractFiles(item, `${prefix}[${index}]`)
                    Object.assign(files, nestedFiles)
                })
            } else if (obj && typeof obj === 'object') {
                Object.entries(obj).forEach(([key, value]) => {
                    const nestedPrefix = prefix ? `${prefix}[${key}]` : key
                    const nestedFiles = extractFiles(value, nestedPrefix)
                    Object.assign(files, nestedFiles)
                })
            }

            return files
        }

        // Helper function to remove File objects from nested structures
        const removeFiles = (obj: any): any => {
            if (obj instanceof File) {
                return null // Remove File objects
            }

            if (Array.isArray(obj)) {
                return obj.map(item => removeFiles(item))
            } else if (obj && typeof obj === 'object') {
                const cleaned: any = {}
                Object.entries(obj).forEach(([key, value]) => {
                    const cleanedValue = removeFiles(value)
                    if (cleanedValue !== null) {
                        cleaned[key] = cleanedValue
                    }
                })
                return cleaned
            }

            return obj
        }

        Object.entries(formState).forEach(([key, value]) => {
            if (value instanceof File) {
                formData.append(key, value)
            } else if (typeof value === 'object') {
                // Extract all File objects from the nested structure
                const files = extractFiles(value, key)
                Object.entries(files).forEach(([fileKey, file]) => {
                    formData.append(fileKey, file)
                })

                // Remove File objects from the structure before JSON.stringify
                const cleanedValue = removeFiles(value)
                formData.append(key, JSON.stringify(cleanedValue))
            } else {
                formData.append(key, value)
            }
        })

        // Append id and parentId to FormData when present (single clean payload for actions)
        if (id) formData.append('id', id)
        if (parentId) formData.append('parentId', parentId)

        let result: ApiResponse
        try {
            result = await onSubmit(formData)
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Something went wrong. Please try again.'
            result = { success: false, message }
            handleError(result)
            setIsSubmitting(false)
            return
        }

        if (result.success) {
            if (urlParam && 'data' in result && (result.data as { id: string }).id) {
                setParam(urlParam, (result.data as { id: string }).id)
                // If we were creating a new record (no id prop), remove the 'add' parameter
                if (!id) {
                    removeSpecificParam(['add'])
                }
            }
        }
        const displayMsg = getErrorDisplayMessage?.(result)
        handleError(displayMsg != null ? { ...result, message: displayMsg } : result)

        if (!keepLoadingOnSuccess || !result.success) {
            setIsSubmitting(false)
        }
    }

    const shouldRenderField = (field: TFormField): boolean => {
        if (!field.dependsOn || field.dependsOn.length === 0) return true
        return field.dependsOn.every(dep => {
            const fieldValue = formState[dep.field]

            if (dep.value === null) {
                return !!fieldValue && fieldValue !== ''
            }

            return fieldValue === dep.value
        })
    }

    const checkboxHandleChange = async (field: TFormField, value: any) => {
        if (field.showConfirmationDialog) {
            let confirmationMessage = ''

            // Use dynamic messages based on current checkbox state if provided
            if (field.confirmationMessageWhenTrue && field.confirmationMessageWhenFalse) {
                confirmationMessage = !!formState[field.name]
                    ? field.confirmationMessageWhenTrue
                    : field.confirmationMessageWhenFalse
            } else if (field.confirmationMessage) {
                confirmationMessage = field.confirmationMessage
            }

            setConfirmationDialog({
                open: true,
                field: field,
                value: value,
                title: field.confirmationTitle || 'Confirm Action',
                message: confirmationMessage
            })
            return
        }

        await executeCheckboxAction(field, value)
    }

    const executeCheckboxAction = async (field: TFormField, value: any) => {
        setUpdatingCheckboxes(prev => ({ ...prev, [field.name]: true }))

        if (field?.customCheckboxFalseAction || field?.customCheckboxTrueAction) {
            if (!value && field?.customCheckboxFalseAction) {
                const response = await field.customCheckboxFalseAction(id || '', parentId)
                if (response?.success) handleChange(field.name, value)
                handleError(response, response.message, [])
            } else if (value && field?.customCheckboxTrueAction) {
                const response = await field.customCheckboxTrueAction(id || '', parentId)
                if (response?.success) handleChange(field.name, value)
                handleError(response, response.message, [])
            }
        } else {
            handleChange(field.name, value)
        }
        setUpdatingCheckboxes(prev => ({ ...prev, [field.name]: false }))
    }

    const handleConfirmAction = async () => {
        if (!confirmationDialog.field) return

        setConfirmationDialog(prev => ({ ...prev, open: false }))
        await executeCheckboxAction(confirmationDialog.field, confirmationDialog.value)

        // Reset confirmation dialog
        setConfirmationDialog({
            open: false,
            field: null,
            value: false,
            title: '',
            message: ''
        })
    }

    const handleCancelAction = () => {
        setConfirmationDialog({
            open: false,
            field: null,
            value: false,
            title: '',
            message: ''
        })
    }

    const renderField = (field: TFormField) => {
        if (!shouldRenderField(field)) return null
        const Component = FIELD_COMPONENTS[field.type]
        if (!Component) return null

        const extraProps = getExtraPropsForField(field, {
            formState,
            onArrayChange: handleArrayChange,
            onAddArrayItem: handleAddArrayItem,
            onRemoveArrayItem: handleRemoveArrayItem,
            onDuplicateArrayItem: handleDuplicateArrayItem,
            updatingCheckboxes,
            onCheckboxChange: checkboxHandleChange,
            turnstileRef: turnstileRef ?? undefined
        })

        const props: FieldRendererProps = {
            field,
            value: formState[field.name],
            error: errors[field.name],
            onChange: handleChange,
            ...extraProps
        }

        return <Component {...props} />
    }

    if (Object.keys(formState).length === 0) return <LoadingAnimation className='!size-7' />

    return (
        <>
            <form className='admin_form_style' onSubmit={handleSubmit}>
                <div
                    className={`${columns === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4' : 'space-y-3 md:space-y-4'}`}
                >
                    {fields.map(field => (
                        <div key={field.name} className={`${field.fullWidth && columns === 2 ? 'md:col-span-2' : ''}`}>
                            {renderField(field)}
                        </div>
                    ))}
                </div>
                <Button
                    id='submit-button'
                    type='submit'
                    variant='default'
                    disabled={pending || isSubmitting}
                    className={`${
                        pending || isSubmitting
                            ? 'bg-primary/50 border-2 border-primary/70 opacity-70'
                            : 'hover:opacity-90 font-semibold shadow-lg ring-2 ring-primary/50'
                    } justify-center items-center px-4 py-4 md:px-5 md:py-5 text-sm font-bold leading-5 h-10 md:h-12 text-center whitespace-nowrap rounded-md max-md:max-w-full border-2 border-primary-foreground/30 min-w-[120px]`}
                >
                    {pending || isSubmitting ? (
                        <span className='flex items-center gap-2'>
                            <IconLoader />
                            {buttonTexts.saving}
                        </span>
                    ) : (
                        buttonTexts.save
                    )}
                </Button>
            </form>

            <AlertDialog open={confirmationDialog.open}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmationDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirmationDialog.message}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={handleCancelAction}>No</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmAction}>Yes</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

export default GenericForm
